import React, { FC, Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Platform, View } from "react-native";
import { useParams } from "react-router";
import { Spinner } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { LookAndLike } from "@lookiero/look-and-like";
import { QueryStatus } from "@lookiero/messaging-react";
import { Body } from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { Text } from "@lookiero/sty-psp-ui";
import { Layout as UiLayout, useScreenSize } from "@lookiero/sty-psp-ui";
import { useViewStyleProfileAnswersByCustomerId } from "../../../projection/styleProfileAnswers/react/useViewStyleProfileAnswersByCustomerId";
import { useViewStyleProfileDefinitionByCustomerId } from "../../../projection/styleProfileDefinition/react/useViewStyleProfileDefinitionByCustomerId";
import { TrackingPage } from "../../../tracking/tracking";
import { useTrackPageView } from "../../../tracking/useTrackPageView";
import { HomeHeader } from "../../components/templates/header/homeHeader/HomeHeader";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages, PREFIX } from "../../i18n/i18n";
import { style as homeStyle } from "./Home.style";
import { Questions } from "./components/questions/Questions";

interface HomeProps {
  readonly layout: UiLayout;
  readonly lookAndLike: LookAndLike;
  readonly onBack: () => void;
  readonly children: ReactNode;
}

const Home: FC<HomeProps> = ({ layout: Layout, lookAndLike: LookAndLike, onBack, children }) => {
  const { slug } = useParams();
  const screenSize = useScreenSize();
  const {
    customer: { customerId, country, segment },
    tradename,
  } = useStaticInfo();

  const style = useMemo(() => homeStyle(), []);

  const titleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.HOME_TITLE,
    prefix: PREFIX,
    values: { segment: segment.toLowerCase() },
  });
  const descriptionText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.HOME_DESCRIPTION,
    prefix: PREFIX,
    values: { segment: segment.toLowerCase() },
  });

  const [questions] = useViewStyleProfileDefinitionByCustomerId({ customerId });
  const [answers, answersStatus] = useViewStyleProfileAnswersByCustomerId({ customerId });

  const [scrollEnabled, setScrollEnabled] = useState(true);

  const answersLoaded = answersStatus === QueryStatus.SUCCESS || Boolean(answers);
  const displayLookAndLike = Platform.OS === "web" && Boolean(answers);

  const [lookAndLikeRenderedError, setLookAndLikeRenderedError] = useState(false);
  const [lookAndLikeRendered, setLookAndLikeRendered] = useState(false);
  const handleOnLookAndLikeFetched = useCallback(() => setLookAndLikeRendered(true), []);
  const handleOnLookAndLikeError = useCallback(() => {
    setLookAndLikeRendered(true);
    setLookAndLikeRenderedError(true);
  }, []);

  useTrackPageView({
    country,
    customerId,
    page: TrackingPage.STYLE,
    segment,
    tradename,
  });

  useEffect(() => setScrollEnabled(!slug), [slug]);

  if (!answersLoaded) {
    return <Spinner testID="spinner" />;
  }

  return (
    <Fragment>
      <Layout header={Platform.OS === "web" ? <HomeHeader onBack={onBack} /> : undefined} scrollEnabled={scrollEnabled}>
        <Body>
          {displayLookAndLike && (
            <View style={style.bodyRow}>
              <LookAndLike
                customerId={customerId}
                style={style.lookAndLike}
                view="style-profile"
                onError={handleOnLookAndLikeError}
                onFetched={handleOnLookAndLikeFetched}
              />
            </View>
          )}

          {(!displayLookAndLike || lookAndLikeRendered) && (
            <>
              {displayLookAndLike && !lookAndLikeRenderedError && screenSize === "S" && <View style={style.divider} />}

              <View style={style.bodyRow}>
                <View style={style.content}>
                  <Text level={3} style={style.title} variant="heading">
                    {titleText}
                  </Text>
                  <Text level={3} style={style.description}>
                    {descriptionText}
                  </Text>
                </View>

                <View style={style.bodyGap}>
                  {questions && answers ? (
                    <Questions answers={answers} questions={questions} />
                  ) : (
                    <Spinner testID="spinner" />
                  )}
                </View>
              </View>
            </>
          )}
        </Body>
      </Layout>
      {children}
    </Fragment>
  );
};

export { Home };
